import en from './en.ts';
import ptBR from './pt-BR.ts';

// Landing-site locales. EN is the source of truth; pt-BR is kept at full
// parity (Brazil is the primary market). react-intl falls back to the EN
// message for any missing key, so partial maps are safe.
export const SUPPORTED_LOCALES = ['en', 'pt-BR'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

// Brazil is the primary market, so a first-time visitor lands in pt-BR.
export const DEFAULT_LOCALE: Locale = 'pt-BR';

// EN stays the message source of truth — every key exists in en.ts — so it's
// merged underneath the active locale to backstop any missing translation.
// This is the locale react-intl uses for `defaultLocale`, NOT what we show.
export const FALLBACK_LOCALE: Locale = 'en';

// Query-string key that pins the language on a shared link, e.g.
// https://caratuva.com/?lang=en — see parseLocaleParam / detectInitialLocale.
export const LOCALE_PARAM = 'lang';

export const LOCALE_LABELS: Record<Locale, string> = {
  en: 'English',
  'pt-BR': 'Português (Brasil)',
};

// Compact codes for the language switcher on narrow viewports, where the full
// label ("Português (Brasil)") would otherwise crowd the header CTA off-screen.
// The full labels above still drive the dropdown list and the accessible name.
export const LOCALE_SHORT_LABELS: Record<Locale, string> = {
  en: 'EN',
  'pt-BR': 'PT',
};

export const messagesByLocale: Record<Locale, Record<string, string>> = {
  en,
  'pt-BR': ptBR,
};

// Shared with the dashboard so a returning seller keeps one language choice
// across caratuva.com → dashboard.caratuva.com.
const LOCAL_STORAGE_KEY = 'caratuva.locale';

// Normalize a raw ?lang= value to a supported locale. Accepts the canonical
// form ('pt-BR'), any case ('pt-br'), or the bare language subtag ('pt', 'en',
// even 'en-US'). Returns null for anything we don't ship so the caller can fall
// back to its normal detection instead of guessing.
export function parseLocaleParam(value: string | null | undefined): Locale | null {
  if (!value) return null;
  const lower = value.toLowerCase();
  const primary = lower.split('-')[0];
  for (const locale of SUPPORTED_LOCALES) {
    const locLower = locale.toLowerCase();
    if (locLower === lower || locLower.split('-')[0] === primary) return locale;
  }
  return null;
}

// Precedence: an explicit ?lang= on the URL (shared-link intent) wins over a
// returning visitor's stored choice, which wins over the browser language,
// which falls back to the pt-BR default. `urlLocale` lets the caller pass a
// value already parsed by the router; omit it to read window.location directly.
export function detectInitialLocale(urlLocale?: Locale | null): Locale {
  if (urlLocale) return urlLocale;
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  const fromUrl = parseLocaleParam(new URLSearchParams(window.location.search).get(LOCALE_PARAM));
  if (fromUrl) return fromUrl;
  const stored = window.localStorage.getItem(LOCAL_STORAGE_KEY);
  if (stored && (SUPPORTED_LOCALES as readonly string[]).includes(stored)) {
    return stored as Locale;
  }
  const nav = window.navigator.language.toLowerCase();
  if (nav.startsWith('pt')) return 'pt-BR';
  if (nav.startsWith('en')) return 'en';
  return DEFAULT_LOCALE;
}

export function persistLocale(locale: Locale): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(LOCAL_STORAGE_KEY, locale);
}
